import { useState } from "react";
import { Link } from "wouter";
import { ChevronRight, MessageCircle, MoreHorizontal, Plus, Users2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Segmented } from "@/components/layout/segmented";
import { PageHeader } from "@/components/layout/page-header";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { EmptyState } from "@/components/data/empty-state";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { StatusChip } from "@/components/mercado/status-chip";
import { Tile, Numerao, ChipsDeSituacao, AvatarProdutor } from "@/components/mercado/painel";
import { useToast } from "@/hooks/use-toast";
import { useCarteiraPainel, useSalvarProdutor } from "@/hooks/use-cargas";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { origemAtual, useNavegacao } from "@/lib/navegacao";
import { usePode } from "@/lib/permissoes";
import { fmtKgBR, fmtMoedaBR, fmtDiaCurto, fmtDataCurtaBR, fmtToneladas } from "@/lib/formato-mercado";
import { cn } from "@/lib/utils";
import { diaCivilNaFazenda } from "@shared/data-fazenda";
import { linkWhatsAppCom } from "@shared/mercado";
import {
  ORDENS_CARTEIRA, PERIODOS_CARTEIRA, SITUACOES_CARTEIRA,
  type CarteiraDTO, type FiltroCarteira, type LinhaCarteiraDTO,
  type OrdemCarteira, type PeriodoCarteira, type SituacaoCarteira,
} from "@shared/mercado-comprador";

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook). Valor igual ao
// padrão não vai para a URL; valor fora da lista cai no padrão.
const PADRAO = { periodo: "30d", situacao: "todos", origem: "todas", ordem: "aberto", q: "" };
const PERMITIDOS = {
  periodo: PERIODOS_CARTEIRA,
  situacao: ["todos", ...SITUACOES_CARTEIRA],
  origem: ["todas", "RIZOMA", "REGISTRO_PROPRIO"],
  ordem: ORDENS_CARTEIRA,
} as const;

const OPCOES_PERIODO: { id: PeriodoCarteira; label: string }[] = [
  { id: "30d", label: "30 dias" },
  { id: "90d", label: "90 dias" },
  { id: "ano", label: "Ano" },
];

const ROTULO_ORDEM: Record<OrdemCarteira, string> = {
  aberto: "Ordenar: maior em aberto",
  comprado: "Ordenar: mais comprado",
  ultima: "Ordenar: compra mais recente",
  nome: "Ordenar: nome",
};

/**
 * O clique numa linha navega? NÃO quando veio de dentro de um elemento marcado
 * com `data-nao-navega` (WhatsApp, Convidar, o ⋯ e o menu dele).
 *
 * ⚠️ POR QUE UMA REGRA E NÃO `stopPropagation`: o menu do ⋯ abre num PORTAL, e o
 * clique em "Editar cadastro" sobe pela árvore do REACT até a linha — um
 * `stopPropagation` no gatilho não o pegaria, e editar o cadastro abriria
 * Romaneios por baixo. Com a marca, o próprio conteúdo do menu diz "não navega".
 * E é testável sem DOM: a regra é pura.
 */
export function cliqueNavega(alvo: { closest?: (seletor: string) => unknown } | null | undefined): boolean {
  return !alvo?.closest?.("[data-nao-navega]");
}

/** A URL de Romaneios aberta por um produtor — nomes REAIS dos parâmetros de lá. */
export function urlRomaneiosDoProdutor(contatoId: string): string {
  return `/mercado/romaneios?produtor=${encodeURIComponent(contatoId)}&periodo=30d`;
}

/**
 * Produtores — o PAINEL da carteira: de quem compro, quanto, quanto devo a cada
 * um e há quanto tempo não compro.
 *
 * ⚠️ NÃO É MAIS CADASTRO. A tela era "Cidade · Romaneios 0 · kg 0" — e o 0 era
 * mentira (o leitor antigo só via romaneio dentro de carga). O cadastro continua,
 * no ⋯ de cada linha; o número é do servidor, da mesma união de Romaneios e da
 * mesma dívida de A pagar. Nenhuma soma é feita aqui.
 *
 * ⚠️ NÃO EXISTE DETALHE DO PRODUTOR. Clicar na linha abre Romaneios filtrado
 * por ele, em 30 dias, com a faixa de resumo no topo: o detalhe de um produtor
 * É a lista dos romaneios dele.
 */
export default function MercadoProdutoresComprador() {
  const { estado, definir, limpar } = useEstadoNaUrl(PADRAO, PERMITIDOS);
  const podeOperar = usePode("mercado.corretor.operar");
  const [editando, setEditando] = useState<LinhaCarteiraDTO | "novo" | null>(null);

  const filtro: FiltroCarteira = {
    periodo: estado.periodo as PeriodoCarteira,
    situacao: estado.situacao !== "todos" ? estado.situacao as SituacaoCarteira : undefined,
    origem: estado.origem !== "todas" ? estado.origem as FiltroCarteira["origem"] : undefined,
    ordem: estado.ordem as OrdemCarteira,
    q: estado.q || undefined,
  };
  const consulta = useCarteiraPainel(filtro);
  const { data } = consulta;
  const aguardando = estadoDaConsulta(consulta, { variante: "cards" });
  const filtrando = estado.situacao !== "todos" || estado.origem !== "todas" || !!estado.q;

  return (
    <CompradorShell className="space-y-4">
      <PageHeader
        titulo="Produtores"
        descricao="De quem você compra: volume, o que está em aberto e quando foi a última compra."
        empilharQuandoEstreito
        // Quem só tem `ver` não ganha um bloco vazio no lugar dos botões.
        acoesSecundarias={podeOperar ? (
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <Button variant="outline" onClick={() => setEditando("novo")} data-testid="button-novo-produtor">
              <Plus className="h-4 w-4 mr-1.5" />Novo produtor
            </Button>
            <Button asChild data-testid="button-novo-romaneio">
              <Link href="/mercado/romaneios/novo" state={origemAtual()}>
                <Plus className="h-4 w-4 mr-1.5" />Novo romaneio
              </Link>
            </Button>
          </div>
        ) : undefined}
      />

      {aguardando ?? (!dadosValidos(data) ? (
        // Um payload fora da forma (não deveria acontecer: `fetchJson` lança em
        // não-2xx) vira aviso NA TELA — e não um throw que o ErrorBoundary do
        // `main.tsx` transforma em app inteiro em branco, como em 13/09.
        <EmptyState icone={Users2} titulo="Não consegui ler a carteira" descricao="Recarregue a página. Se continuar, avise o suporte." />
      ) : (
        <>
          <Tiles dados={data} />
          <Filtros estado={estado} definir={definir} contagens={data.contagens} />
          {data.linhas.length === 0 ? (
            filtrando ? (
              <EmptyState
                icone={Users2}
                titulo="Nenhum produtor com esses filtros."
                descricao="Tente outra situação, outra origem ou limpe a busca."
                acao={<Button variant="outline" onClick={limpar} data-testid="button-limpar-filtros">Limpar filtros</Button>}
              />
            ) : (
              <EmptyState
                icone={Users2}
                titulo="Sua carteira está vazia"
                descricao="Produtores entram aqui sozinhos quando formalizam um romaneio para você — ou quando você registra um romaneio deles."
              />
            )
          ) : (
            <>
              <TabelaProdutores dados={data} onEditar={p => setEditando(p)} podeOperar={podeOperar} />
              <CartoesProdutores dados={data} />
            </>
          )}
        </>
      ))}

      <DialogProdutor alvo={editando} onFechar={() => setEditando(null)} />
    </CompradorShell>
  );
}

function dadosValidos(d: unknown): d is CarteiraDTO {
  const c = d as CarteiraDTO | null | undefined;
  return !!c && typeof c === "object" && Array.isArray(c.linhas) && !!c.kpis && !!c.contagens && !!c.totais;
}

// ─── Os quatro números ───────────────────────────────────────────────────────

function Tiles({ dados }: { dados: CarteiraDTO }) {
  const { comprando, comprado, emAberto, vencido } = dados.kpis;
  const periodo = dados.rotuloPeriodo.toUpperCase();
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tiles-produtores">
      <Tile rotulo={`Comprando · ${periodo}`}>
        <Numerao valor={String(comprando.n)} unidade={`de ${comprando.total} ${comprando.total === 1 ? "produtor" : "produtores"}`} testId="tile-comprando" />
        <p className="text-xs text-muted-foreground">
          {comprando.noRizoma} no Rizoma · {comprando.registroProprio} de registro próprio
        </p>
      </Tile>

      <Tile rotulo={`Comprado · ${periodo}`}>
        <Numerao valor={fmtToneladas(comprado.kg).replace(" t", "")} unidade="t" testId="tile-comprado" />
        <p className="text-xs text-muted-foreground">
          {fmtMoedaBR(comprado.valor)}
          {comprado.precoMedioKg != null && <> · média {fmtMoedaBR(comprado.precoMedioKg)}/kg</>}
        </p>
      </Tile>

      {/* ⚠️ EM ABERTO E VENCIDO IGNORAM O PERÍODO — dívida antiga continua
          dívida. A mesma regra do tile Pendentes de Romaneios. */}
      <Tile rotulo="Em aberto" alerta={emAberto.valor > 0}>
        <Numerao valor={fmtMoedaBR(emAberto.valor)} testId="tile-em-aberto" />
        <p className="text-xs text-muted-foreground">
          {emAberto.documentos} {emAberto.documentos === 1 ? "documento" : "documentos"} · {emAberto.produtores} {emAberto.produtores === 1 ? "produtor" : "produtores"}
        </p>
        {emAberto.valor > 0 && (
          <Link href="/mercado/pagar" state={origemAtual()} className="mt-auto text-xs font-semibold text-primary" data-testid="link-ver-a-pagar">
            Ver no A pagar →
          </Link>
        )}
      </Tile>

      <Tile rotulo="Vencido" perigo={vencido.valor > 0}>
        <Numerao
          valor={fmtMoedaBR(vencido.valor)}
          className={vencido.valor > 0 ? "text-destructive" : undefined}
          testId="tile-vencido"
        />
        <p className="text-xs text-muted-foreground">
          {vencido.valor > 0
            ? `${vencido.produtores} ${vencido.produtores === 1 ? "produtor" : "produtores"}`
            : "nada vencido"}
        </p>
      </Tile>
    </div>
  );
}

// ─── Filtros ─────────────────────────────────────────────────────────────────

function Filtros({
  estado, definir, contagens,
}: {
  estado: typeof PADRAO;
  definir: (p: Partial<typeof PADRAO>) => void;
  contagens: CarteiraDTO["contagens"];
}) {
  return (
    <div className="space-y-2.5">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {/* Três opções cabem em 390 px — medido: o segmentado de Romaneios, com
            cinco, é que precisou virar Select no celular. */}
        <Segmented<PeriodoCarteira>
          valor={estado.periodo as PeriodoCarteira}
          onChange={v => definir({ periodo: v })}
          testId="seg-periodo-produtores"
          className="self-start"
          opcoes={OPCOES_PERIODO}
        />
        <Select value={estado.origem} onValueChange={v => definir({ origem: v })}>
          <SelectTrigger className="sm:w-48" data-testid="filtro-origem-produtores"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas as origens</SelectItem>
            <SelectItem value="RIZOMA">No Rizoma</SelectItem>
            <SelectItem value="REGISTRO_PROPRIO">Registro próprio</SelectItem>
          </SelectContent>
        </Select>
        <Select value={estado.ordem} onValueChange={v => definir({ ordem: v })}>
          <SelectTrigger className="sm:w-60" data-testid="filtro-ordem-produtores"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ORDENS_CARTEIRA.map(o => <SelectItem key={o} value={o}>{ROTULO_ORDEM[o]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          value={estado.q}
          onChange={e => definir({ q: e.target.value })}
          placeholder="Buscar nome, apelido ou telefone"
          className="sm:ml-auto sm:w-64"
          aria-label="Buscar produtor por nome, apelido ou telefone"
          data-testid="busca-produtores"
        />
      </div>
      <ChipsDeSituacao<string>
        opcoes={[
          { id: "todos", label: "Todos", n: contagens.todos },
          { id: "COM_ABERTO", label: "Com saldo em aberto", n: contagens.COM_ABERTO },
          { id: "COM_VENCIDO", label: "Com vencido", n: contagens.COM_VENCIDO },
          { id: "SEM_COMPRA", label: "Sem compra no período", n: contagens.SEM_COMPRA },
        ]}
        valor={estado.situacao}
        onChange={s => definir({ situacao: s })}
        testId="chips-produtores"
      />
    </div>
  );
}

// ─── Tabela (desktop) ────────────────────────────────────────────────────────

/**
 * A linha INTEIRA navega; WhatsApp, Convidar e ⋯ não (`cliqueNavega`), e cada
 * um tem foco de teclado próprio. A linha é `role="link"` com Enter — um `<tr>`
 * com onClick sozinho seria invisível para quem navega por teclado.
 */
function TabelaProdutores({
  dados, onEditar, podeOperar,
}: { dados: CarteiraDTO; onEditar: (p: LinhaCarteiraDTO) => void; podeOperar: boolean }) {
  const { abrir } = useNavegacao();
  const hoje = diaCivilNaFazenda(new Date());
  const per = dados.rotuloPeriodo;

  return (
    <div className="hidden md:block rounded-lg border bg-card overflow-x-auto" data-testid="tabela-produtores">
      {/* Cabeçalho PODE quebrar em duas linhas ("Romaneios · / 30 dias"); número
          não. É o que faz as oito colunas caberem na largura do desktop sem
          empurrar a página — a tabela rola dentro do cartão, se preciso, e
          nunca a tela inteira. */}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="text-left px-3 py-2.5">Produtor</th>
            <th className="text-right px-3 py-2.5">Romaneios · {per}</th>
            <th className="text-right px-3 py-2.5">kg · {per}</th>
            <th className="text-right px-3 py-2.5">Comprado · {per}</th>
            <th className="text-right px-3 py-2.5">Em aberto</th>
            <th className="text-left px-3 py-2.5">Próximo vencimento</th>
            <th className="text-left px-3 py-2.5">Última compra</th>
            <th className="px-2 py-2.5"><span className="sr-only">Ações</span></th>
          </tr>
        </thead>
        <tbody>
          {dados.linhas.map(p => {
            const ir = () => abrir(urlRomaneiosDoProdutor(p.contatoId));
            return (
              <tr
                key={p.contatoId}
                role="link"
                tabIndex={0}
                onClick={e => { if (cliqueNavega(e.target as Element)) ir(); }}
                onKeyDown={e => { if (e.key === "Enter" && cliqueNavega(e.target as Element)) ir(); }}
                className="border-b last:border-b-0 cursor-pointer hover:bg-muted/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                data-testid={`produtor-${p.contatoId}`}
              >
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-[240px]">
                    <AvatarProdutor nome={p.nome} noRizoma={p.noRizoma} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="font-semibold">{p.nome}</span>
                        <SeloOrigem noRizoma={p.noRizoma} />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {subtitulo(p)}
                        <Convidar produtor={p} />
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <span className="font-mono tabular-nums font-semibold">{p.periodo.romaneios}</span>
                  <span className="block text-[11px] text-muted-foreground">
                    {p.periodo.romaneios === 0 ? "" : p.periodo.cargas === 0 ? "nenhum em carga" : `${p.periodo.cargas} em carga`}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {p.periodo.kg > 0 ? (
                    <>
                      <span className="font-mono tabular-nums">{fmtKgBR(p.periodo.kg)}</span>
                      <BarraClassificacao linha={p} />
                    </>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {p.periodo.valor > 0 ? (
                    <>
                      <span className="font-mono tabular-nums font-semibold">{fmtMoedaBR(p.periodo.valor)}</span>
                      {p.periodo.precoMedioKg != null && (
                        <span className="block text-[11px] text-muted-foreground">{fmtMoedaBR(p.periodo.precoMedioKg)}/kg</span>
                      )}
                    </>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {p.emAberto > 0 ? (
                    <>
                      <span className="font-mono tabular-nums font-semibold">{fmtMoedaBR(p.emAberto)}</span>
                      <span className={cn("block text-[11px]", p.vencido > 0 ? "text-destructive" : "text-muted-foreground")}>
                        {p.vencido > 0
                          ? `${fmtMoedaBR(p.vencido)} vencido`
                          : `${p.documentosEmAberto} ${p.documentosEmAberto === 1 ? "documento" : "documentos"}`}
                      </span>
                    </>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {p.proximoVencimento ? (
                    <>
                      <span className="font-semibold">{fmtDiaCurto(p.proximoVencimento)}</span>
                      <span className={cn("block text-[11px]", diasAte(p.proximoVencimento, hoje) <= 3 ? "text-atencao font-semibold" : "text-muted-foreground")}>
                        {distancia(p.proximoVencimento, hoje)}
                      </span>
                    </>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {p.ultimaCompraEm ? (
                    <>
                      <span className="font-mono tabular-nums">{fmtDataCurtaBR(p.ultimaCompraEm)}</span>
                      <span className="block text-[11px] text-muted-foreground">{distancia(p.ultimaCompraEm, hoje)}</span>
                    </>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-2 py-2.5">
                  <div className="flex items-center justify-end gap-1" data-nao-navega>
                    {p.telefone && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                        <a
                          href={linkWhatsAppCom(p.telefone, "")}
                          target="_blank" rel="noreferrer"
                          aria-label={`WhatsApp de ${p.nome}`}
                          data-testid={`whatsapp-${p.contatoId}`}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    {podeOperar && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Mais ações de ${p.nome}`} data-testid={`menu-${p.contatoId}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" data-nao-navega>
                          <DropdownMenuItem onClick={() => onEditar(p)} data-testid={`editar-${p.contatoId}`}>Editar cadastro</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-muted/40 font-semibold">
            <td className="px-3 py-2.5">
              {dados.totais.produtores} {dados.totais.produtores === 1 ? "produtor" : "produtores"}
            </td>
            <td className="px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap">{dados.totais.romaneios}</td>
            <td className="px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap">{fmtKgBR(dados.totais.kg)}</td>
            <td className="px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap">{fmtMoedaBR(dados.totais.valor)}</td>
            <td className="px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap">{fmtMoedaBR(dados.totais.emAberto)}</td>
            <td colSpan={3} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─── Cartões (celular) ───────────────────────────────────────────────────────

/** O cartão INTEIRO navega. Três números: o período, o em aberto, a última. */
function CartoesProdutores({ dados }: { dados: CarteiraDTO }) {
  const { abrir } = useNavegacao();
  const hoje = diaCivilNaFazenda(new Date());
  return (
    <div className="md:hidden space-y-2.5" data-testid="cartoes-produtores">
      {dados.linhas.map(p => (
        <button
          key={p.contatoId}
          type="button"
          onClick={() => abrir(urlRomaneiosDoProdutor(p.contatoId))}
          className="w-full text-left rounded-lg border bg-card p-3.5 hover-elevate"
          data-testid={`cartao-produtor-${p.contatoId}`}
        >
          <div className="flex items-start gap-2.5">
            <AvatarProdutor nome={p.nome} noRizoma={p.noRizoma} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <span className="font-semibold">{p.nome}</span>
                <SeloOrigem noRizoma={p.noRizoma} />
              </div>
              <p className="text-xs text-muted-foreground">{subtitulo(p)}</p>
            </div>
          </div>
          <dl className="grid grid-cols-3 gap-2 mt-2.5">
            <div>
              <dt className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{dados.rotuloPeriodo}</dt>
              <dd className="font-mono tabular-nums font-semibold text-[13px] whitespace-nowrap">{p.periodo.kg > 0 ? fmtToneladas(p.periodo.kg) : "—"}</dd>
              <dd className="text-[11px] text-muted-foreground">
                {p.periodo.romaneios} rom.{p.periodo.valor > 0 && <> · {fmtMoedaBR(p.periodo.valor)}</>}
              </dd>
            </div>
            <div>
              <dt className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Em aberto</dt>
              <dd className={cn("font-mono tabular-nums font-semibold text-[13px] whitespace-nowrap", p.vencido > 0 && "text-destructive")}>
                {p.emAberto > 0 ? fmtMoedaBR(p.emAberto) : "—"}
              </dd>
              <dd className="text-[11px] text-muted-foreground">
                {p.vencido > 0 ? "vencido" : p.proximoVencimento ? `vence ${fmtDataCurtaBR(p.proximoVencimento)}` : ""}
              </dd>
            </div>
            <div>
              <dt className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Última</dt>
              <dd className="font-mono tabular-nums font-semibold text-[13px] whitespace-nowrap">{p.ultimaCompraEm ? fmtDataCurtaBR(p.ultimaCompraEm) : "—"}</dd>
              <dd className="text-[11px] text-muted-foreground">{p.ultimaCompraEm ? distancia(p.ultimaCompraEm, hoje) : ""}</dd>
            </div>
          </dl>
        </button>
      ))}
    </div>
  );
}

// ─── Peças ───────────────────────────────────────────────────────────────────

function SeloOrigem({ noRizoma }: { noRizoma: boolean }) {
  return noRizoma
    ? <StatusChip kind="pago">Rizoma</StatusChip>
    : <StatusChip kind="aberta">Registro próprio</StatusChip>;
}

/** "Fazenda X · Cidade" — sem repetir o nome quando a fazenda É o nome. */
function subtitulo(p: LinhaCarteiraDTO): string {
  const fazenda = p.fazenda && p.fazenda !== p.nome ? p.fazenda : null;
  return [fazenda, p.cidade].filter(Boolean).join(" · ") || "—";
}

/** A barra de classificação — mesmas cores de Romaneios (cor por posição). */
function BarraClassificacao({ linha }: { linha: LinhaCarteiraDTO }) {
  const total = linha.periodo.porClassificacao.reduce((a, c) => a + c.kg, 0);
  if (total <= 0) return null;
  return (
    <div
      className="h-1.5 rounded-full bg-muted overflow-hidden flex mt-1 ml-auto w-24"
      title={linha.periodo.porClassificacao.map(c => `${c.classificacao} ${Math.round((c.kg / total) * 100)}%`).join(" · ")}
      aria-hidden
    >
      {linha.periodo.porClassificacao.map((c, idx) => (
        <div key={c.classificacao} style={{ width: `${(c.kg / total) * 100}%`, background: `hsl(var(--chart-${(idx % 5) + 1}))` }} />
      ))}
    </div>
  );
}

/**
 * "Convidar" — só registro próprio com o `/p/` do último registro. O convite É
 * aquele link: ele abre vendo os próprios números, e o botão de criar conta
 * está na mesma tela. Não navega a linha.
 */
function Convidar({ produtor }: { produtor: LinhaCarteiraDTO }) {
  if (produtor.noRizoma || !produtor.telefone || !produtor.ultimoItemToken) return null;
  return (
    <>
      {" · "}
      <a
        href={linkWhatsAppCom(produtor.telefone, convite(produtor.nome, produtor.ultimoItemToken))}
        target="_blank" rel="noreferrer"
        data-nao-navega
        className="font-semibold text-primary"
        data-testid={`button-convidar-${produtor.contatoId}`}
      >
        Convidar
      </a>
    </>
  );
}

/**
 * O convite curto — sem acento, como a chamada de oferta.
 *
 * ⚠️ A URL fica em LINHA PRÓPRIA e sem pontuação depois: ponto colado no fim do
 * link entra no que o WhatsApp transforma em link. Texto de WhatsApp é BMP-only.
 */
function convite(nome: string, token: string): string {
  const primeiro = nome.trim().split(/\s+/)[0] ?? "";
  return [
    `Oi ${primeiro}, registro nossas cargas no Rizoma.`,
    "Confira os numeros da sua ultima carga e crie seu acesso:",
    // `typeof window`: o convite é montado DURANTE o render (vira o `href`), e
    // o render sem navegador (os testes de marcação) não tem `window`.
    `${typeof window !== "undefined" ? window.location.origin : ""}/p/${token}`,
  ].join("\n");
}

function diasAte(data: string, hoje: string): number {
  return Math.round((Date.parse(`${data}T12:00:00Z`) - Date.parse(`${hoje}T12:00:00Z`)) / 86_400_000);
}

/** "em 2 d", "hoje", "há 6 d". */
function distancia(data: string, hoje: string): string {
  const n = diasAte(data, hoje);
  return n === 0 ? "hoje" : n > 0 ? `em ${n} d` : `há ${-n} d`;
}

// ─── Cadastro (Novo produtor / Editar cadastro) ──────────────────────────────

/**
 * O CADASTRO — o mesmo formulário para criar e para editar. É o que a tela
 * inteira era antes; agora mora no ⋯ da linha e no "Novo produtor".
 */
function DialogProdutor({ alvo, onFechar }: { alvo: LinhaCarteiraDTO | "novo" | null; onFechar: () => void }) {
  const { toast } = useToast();
  const salvar = useSalvarProdutor();
  const [nome, setNome] = useState("");
  const [apelidos, setApelidos] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cidade, setCidade] = useState("");

  if (!alvo) return null;
  const produtor = alvo === "novo" ? null : alvo;

  return (
    // `key` força o remount a cada produtor — sem isso o formulário guardaria o
    // nome do anterior.
    <Dialog key={produtor?.contatoId ?? "novo"} open onOpenChange={o => !o && onFechar()}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={() => {
          setNome(produtor?.nome ?? "");
          setApelidos((produtor?.apelidos ?? []).join(", "));
          setTelefone(produtor?.telefone ?? "");
          setCidade(produtor?.cidade ?? "");
        }}
      >
        <DialogHeader><DialogTitle>{produtor ? "Editar cadastro" : "Novo produtor"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-nome">Nome</Label>
            <Input id="edit-nome" value={nome} onChange={e => setNome(e.target.value)} data-testid="input-edit-nome" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-apelidos">Apelidos</Label>
            <Input
              id="edit-apelidos" value={apelidos} onChange={e => setApelidos(e.target.value)}
              placeholder="Zé do Alto, compadre" data-testid="input-edit-apelidos"
            />
            <p className="text-xs text-muted-foreground">
              Separados por vírgula. A busca procura por eles também — é o que evita o segundo cadastro do mesmo produtor.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="edit-telefone">WhatsApp</Label>
              <Input id="edit-telefone" inputMode="tel" value={telefone} onChange={e => setTelefone(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cidade">Cidade</Label>
              <Input id="edit-cidade" value={cidade} onChange={e => setCidade(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            onClick={() => salvar.mutate(
              {
                id: produtor?.contatoId,
                nome: nome.trim(),
                apelidos: apelidos.split(",").map(a => a.trim()).filter(Boolean),
                telefone: telefone.trim() || null,
                cidade: cidade.trim() || null,
              },
              {
                onSuccess: onFechar,
                onError: (e: Error) => toast({ title: "Não consegui salvar", description: e.message, variant: "destructive" }),
              },
            )}
            disabled={!nome.trim() || salvar.isPending}
            data-testid="button-salvar-produtor"
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
